import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Award, ChevronDown, ChevronUp, Edit3, MessageSquare, Search, Star } from "lucide-react";
import { toast } from "sonner";

const SECTION_LABEL = { mid_year: "Mid-Year Review", year_end: "Year-End Review" };

const RatingBadge = ({ name }) => {
  const cls = name === "Exceeds Expectation"
    ? "bg-emerald-100 text-emerald-800"
    : name === "Meets Expectation"
      ? "bg-sky-100 text-sky-800"
      : "bg-amber-100 text-amber-800";
  return <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${cls}`} data-testid={`rating-badge-${name}`}>{name}</span>;
};

const ReviewCard = ({ review, section, canEdit, canComment, onEdit, onComment }) => (
  <div className="border border-summit-hairline rounded-lg bg-white p-5" data-testid={`review-card-${section}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{SECTION_LABEL[section]}</div>
        <h3 className="font-display text-lg font-medium tracking-tight mt-0.5">
          {review ? (review.rating_name) : "Not yet submitted"}
        </h3>
      </div>
      {review && <RatingBadge name={review.rating_name} />}
    </div>
    {review ? (
      <>
        <div className="mt-3 font-mono text-[10px] text-gray-400 uppercase tracking-widest">
          Reviewed by {review.manager_name} · {new Date(review.updated_at).toLocaleDateString()}
        </div>
        <p className="mt-3 text-sm whitespace-pre-wrap text-gray-700 leading-relaxed">
          {review.review_text || <span className="text-gray-400">No written feedback.</span>}
        </p>
        <div className="mt-4 pt-4 border-t border-summit-hairline">
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-500">
            <MessageSquare className="w-3 h-3" /> Employee comment
          </div>
          {review.employee_comment ? (
            <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{review.employee_comment}</p>
          ) : (
            <p className="mt-2 text-sm text-gray-400 italic">No comment yet.</p>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {canEdit && (
            <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-review-${section}`}>
              <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit review
            </Button>
          )}
          {canComment && (
            <Button size="sm" variant="outline" onClick={onComment} data-testid={`comment-review-${section}`}>
              <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> {review.employee_comment ? "Edit comment" : "Add comment"}
            </Button>
          )}
        </div>
      </>
    ) : (
      <div className="mt-3">
        <p className="text-sm text-gray-500">Awaiting review from the direct manager.</p>
        {canEdit && (
          <Button size="sm" className="mt-3 bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={onEdit} data-testid={`create-review-${section}`}>
            <Star className="w-3.5 h-3.5 mr-1.5" /> Post {SECTION_LABEL[section]}
          </Button>
        )}
      </div>
    )}
  </div>
);

export default function Reviews() {
  const { user } = useAuth();
  const [ratings, setRatings] = useState([]);
  const [directReports, setDirectReports] = useState([]);
  // Admins search across ALL users; managers search only their direct reports.
  // Regular users don't see a picker at all.
  const [searchableUsers, setSearchableUsers] = useState([]);
  const [reportSearch, setReportSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(user.id);
  const [data, setData] = useState({ year: new Date().getFullYear(), current: [], archive: [], subject: null });
  const [archiveOpen, setArchiveOpen] = useState(false);

  const [editModal, setEditModal] = useState({ open: false, section: "", rating_id: "", review_text: "" });
  const [commentModal, setCommentModal] = useState({ open: false, review_id: "", comment: "" });

  useEffect(() => {
    api.get("/review-ratings").then((r) => setRatings(r.data)).catch(() => {});
    if (user.can_have_reports) {
      api.get("/direct-reports").then((r) => setDirectReports(r.data)).catch(() => {});
    }
    // Admins can view any user's reviews — pull the full user list. Managers
    // (non-admin) fall back to their direct reports list.
    if (user.role === "admin") {
      api.get("/admin/users").then((r) => {
        setSearchableUsers((r.data || []).filter((u) => u.id !== user.id));
      }).catch(() => {});
    } else if (user.can_have_reports) {
      api.get("/direct-reports").then((r) => setSearchableUsers(r.data || [])).catch(() => {});
    }
  }, [user.can_have_reports, user.role, user.id]);

  const loadFor = useCallback(async (uid) => {
    try {
      const path = uid === user.id ? "/reviews/me" : `/reviews/user/${uid}`;
      const { data } = await api.get(path);
      setData({
        year: data.year,
        current: data.current || [],
        archive: data.archive || [],
        subject: data.subject || null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reviews");
    }
  }, [user.id]);

  useEffect(() => { loadFor(selectedUserId); }, [selectedUserId, loadFor]);

  const isSelf = selectedUserId === user.id;
  const isAdmin = user.role === "admin";
  // The direct-reports list only contains subordinates of the caller — so if the
  // selected user is in that list, we're their direct manager.
  const isDirectManager = !isSelf && directReports.some((r) => r.id === selectedUserId);
  const canPostReview = isAdmin || isDirectManager;

  const currentBySection = useMemo(() => {
    const map = { mid_year: null, year_end: null };
    for (const r of data.current) map[r.section] = r;
    return map;
  }, [data]);

  const openEdit = (section) => {
    const existing = currentBySection[section];
    setEditModal({
      open: true,
      section,
      rating_id: existing?.rating_id || (ratings[0]?.id || ""),
      review_text: existing?.review_text || "",
    });
  };

  const submitReview = async () => {
    if (!editModal.rating_id) { toast.error("Pick a rating"); return; }
    try {
      await api.post(`/reviews/user/${selectedUserId}`, {
        section: editModal.section,
        rating_id: editModal.rating_id,
        review_text: editModal.review_text,
      });
      toast.success("Review saved");
      setEditModal({ open: false, section: "", rating_id: "", review_text: "" });
      await loadFor(selectedUserId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const openComment = (review) => {
    setCommentModal({ open: true, review_id: review.id, comment: review.employee_comment || "" });
  };

  const submitComment = async () => {
    try {
      await api.post(`/reviews/${commentModal.review_id}/comment`, { comment: commentModal.comment });
      toast.success("Comment saved");
      setCommentModal({ open: false, review_id: "", comment: "" });
      await loadFor(selectedUserId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const archiveGrouped = useMemo(() => {
    const g = {};
    for (const r of data.archive) {
      if (!g[r.year]) g[r.year] = { mid_year: null, year_end: null };
      g[r.year][r.section] = r;
    }
    return g;
  }, [data]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
            <Award className="w-3 h-3" /> Performance
          </div>
          <h1 className="font-display text-3xl font-medium tracking-tight mt-1" data-testid="reviews-heading">
            Reviews · {data.year}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isSelf
              ? "Your Mid-Year and Year-End reviews. Older years are in the archive below."
              : `Reviews for ${data.subject?.display_name || ""}`}
          </p>
        </div>
      </div>

      {/* Reviews picker — search bar visible ONLY for managers and admins.
          Regular users see just their own Mid-Year / Year-End cards below. */}
      {(user.role === "admin" || user.can_have_reports) && searchableUsers.length > 0 && (
        <div className="mt-6 max-w-md">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={reportSearch}
              onChange={(e) => setReportSearch(e.target.value)}
              placeholder={user.role === "admin" ? "Search users by name, employee #, title…" : "Search direct reports…"}
              className="w-full h-10 pl-9 pr-3 rounded-md border border-summit-hairline text-sm bg-white"
              data-testid="reviews-search-input"
            />
          </div>
          {reportSearch.trim().length > 0 && (
            <div
              className="mt-2 border border-summit-hairline rounded-md bg-white shadow-sm max-h-64 overflow-y-auto"
              data-testid="reviews-search-results"
            >
              <button
                type="button"
                onClick={() => { setSelectedUserId(user.id); setReportSearch(""); }}
                className={`w-full text-left px-3 py-2 text-sm border-b border-summit-hairline hover:bg-summit-glacier-soft ${
                  selectedUserId === user.id ? "bg-summit-glacier text-summit-primary font-medium" : ""
                }`}
                data-testid="reviews-search-self"
              >
                Me · {user.display_name}
              </button>
              {searchableUsers
                .filter((r) => {
                  const q = reportSearch.trim().toLowerCase();
                  return (
                    (r.last_name || "").toLowerCase().includes(q) ||
                    (r.first_name || "").toLowerCase().includes(q) ||
                    (r.first_initial || "").toLowerCase().includes(q) ||
                    (r.employee_number || "").toString().toLowerCase().includes(q) ||
                    (r.job_title || "").toLowerCase().includes(q) ||
                    (r.display_name || "").toLowerCase().includes(q)
                  );
                })
                .slice(0, 25)
                .map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => { setSelectedUserId(r.id); setReportSearch(""); }}
                    className={`w-full text-left px-3 py-2 text-sm border-b last:border-0 border-summit-hairline hover:bg-summit-glacier-soft ${
                      selectedUserId === r.id ? "bg-summit-glacier text-summit-primary font-medium" : ""
                    }`}
                    data-testid={`reviews-search-result-${r.employee_number}`}
                  >
                    <div>{r.last_name}, {r.first_initial} · {r.job_title || ""}</div>
                    <div className="text-xs text-gray-500 font-mono">#{r.employee_number}{r.department ? ` · ${r.department}` : ""}</div>
                  </button>
                ))}
            </div>
          )}
          {selectedUserId !== user.id && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="text-gray-500">Viewing:</span>
              <span className="font-mono px-2 py-0.5 rounded bg-summit-glacier text-summit-primary" data-testid="reviews-current-subject">
                {data.subject?.display_name || "…"}
              </span>
              <button
                type="button"
                onClick={() => setSelectedUserId(user.id)}
                className="text-summit-primary hover:underline"
                data-testid="reviews-back-to-self"
              >
                Back to my reviews
              </button>
            </div>
          )}
        </div>
      )}


      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <ReviewCard
          review={currentBySection.mid_year}
          section="mid_year"
          canEdit={canPostReview}
          canComment={isSelf && !!currentBySection.mid_year}
          onEdit={() => openEdit("mid_year")}
          onComment={() => openComment(currentBySection.mid_year)}
        />
        <ReviewCard
          review={currentBySection.year_end}
          section="year_end"
          canEdit={canPostReview}
          canComment={isSelf && !!currentBySection.year_end}
          onEdit={() => openEdit("year_end")}
          onComment={() => openComment(currentBySection.year_end)}
        />
      </div>

      {/* Archive */}
      <div className="mt-8 border border-summit-hairline rounded-lg bg-white">
        <button
          type="button"
          onClick={() => setArchiveOpen((o) => !o)}
          className="w-full px-5 py-4 flex items-center justify-between text-left"
          data-testid="archive-toggle"
        >
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Archive</div>
            <div className="font-display text-base font-medium tracking-tight mt-0.5">
              {Object.keys(archiveGrouped).length === 0
                ? "No archived reviews yet"
                : `${Object.keys(archiveGrouped).length} archived year${Object.keys(archiveGrouped).length === 1 ? "" : "s"}`}
            </div>
          </div>
          {archiveOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {archiveOpen && Object.keys(archiveGrouped).length > 0 && (
          <div className="border-t border-summit-hairline p-5 space-y-5">
            {Object.keys(archiveGrouped).sort((a, b) => Number(b) - Number(a)).map((yr) => (
              <div key={yr} data-testid={`archive-year-${yr}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">{yr}</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {["mid_year", "year_end"].map((sec) => {
                    const rev = archiveGrouped[yr][sec];
                    if (!rev) return null;
                    return (
                      <div key={sec} className="border border-summit-hairline rounded-md p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-medium">{SECTION_LABEL[sec]}</div>
                          <RatingBadge name={rev.rating_name} />
                        </div>
                        {rev.review_text && (
                          <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">{rev.review_text}</p>
                        )}
                        {rev.employee_comment && (
                          <p className="mt-2 text-xs text-gray-500 italic whitespace-pre-wrap">
                            Employee: {rev.employee_comment}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit review modal */}
      <Dialog open={editModal.open} onOpenChange={(o) => !o && setEditModal({ open: false, section: "", rating_id: "", review_text: "" })}>
        <DialogContent className="max-w-lg" data-testid="review-edit-modal">
          <DialogHeader>
            <DialogTitle>{SECTION_LABEL[editModal.section]}</DialogTitle>
            <DialogDescription>
              {isSelf ? "Post your own review (admin override)" : `Review for ${data.subject?.display_name || ""}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500 mb-2 block">Rating</Label>
              <div className="grid gap-2">
                {ratings.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setEditModal((m) => ({ ...m, rating_id: r.id }))}
                    className={`text-left px-3 py-2 rounded-md border font-mono text-xs uppercase tracking-widest transition-colors ${
                      editModal.rating_id === r.id
                        ? "bg-summit-primary text-white border-summit-primary"
                        : "bg-white text-gray-700 border-summit-hairline hover:bg-summit-glacier"
                    }`}
                    data-testid={`rating-option-${r.id}`}
                  >{r.name}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Written feedback</Label>
              <Textarea
                value={editModal.review_text}
                onChange={(e) => setEditModal((m) => ({ ...m, review_text: e.target.value }))}
                rows={5}
                placeholder="Strengths, growth areas, focus for next cycle…"
                data-testid="review-text-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, section: "", rating_id: "", review_text: "" })}>Cancel</Button>
            <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitReview} data-testid="review-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comment modal */}
      <Dialog open={commentModal.open} onOpenChange={(o) => !o && setCommentModal({ open: false, review_id: "", comment: "" })}>
        <DialogContent className="max-w-md" data-testid="review-comment-modal">
          <DialogHeader>
            <DialogTitle>Your comment</DialogTitle>
            <DialogDescription>Share your thoughts on this review. Visible to your manager and admins.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={commentModal.comment}
            onChange={(e) => setCommentModal((m) => ({ ...m, comment: e.target.value }))}
            rows={4}
            placeholder="Thank your manager, add context, agree/disagree…"
            data-testid="review-comment-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommentModal({ open: false, review_id: "", comment: "" })}>Cancel</Button>
            <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitComment} data-testid="review-comment-save">Save comment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
